/**
 * Settings → Test Code — WHICH columns each detail tab shows, in order.
 *
 * <p>Extracted from TestCodeDetailTabs.tsx on 2026-10-30 so the grids and the
 * parity test read the SAME list. They used to be two lists: an audit that
 * compared tabs and actions found a missing Methods tab but could not see that
 * four tabs were short of columns — Surrogates showed 9 of GoLive's 23,
 * Internal Standards 10 of 15, the QC grids 16 of 27 — so values a lab had
 * entered in XAF were not on screen at all.</p>
 *
 * <p>Each list is GoLive's own ColumnInfo order for the view named above it
 * (BTLIMS.Web/Model.xafml). Keeping the order makes the two directly
 * comparable; testCodeColumnParity.test.ts does that comparison.</p>
 */

/** Header text per row field. The grids render it and the parity test reads it,
 *  so a column cannot be renamed in one place only. */
export const LABEL: Record<string, string> = {
  parameter: "Parameter", surrogate: "Surrogate", internalStandard: "InternalStandard",
  sort: "Sort", component: "Component",
  defaultResult: "DefaultResult", defaultUnits: "DefaultUnits",
  finalDefaultResult: "FinalDefaultResult", finalDefaultUnits: "FinalDefaultUnits",
  rl: "RL", mdl: "MDL", mdlSpikeAmount: "MDLSpikeAmount", mdlSpikeUnits: "MDLSpikeUnits",
  rsd: "RSD", regulatoryLimit: "RegulatoryLimit", mcl: "MCL", loq: "LOQ", uql: "UQL",
  lcLimit: "LCLimit", ucLimit: "UCLimit", reLcLimit: "RELCLimit", reUcLimit: "REUCLimit",
  sigFig: "SigFig", cutOff: "CutOff", decimal: "Decimal",
  synonym: "Synonym", accreditation: "Accreditation", comment: "Comment", retireDate: "RetireDate",

  /* ⚠ The SURROGATE's own fields, captioned as GoLive captions them
     (Testparameter_ListView_Test_Surrogates idx 5-7). They were labelled
     SpikeAmount / SpikeAmountUnits / LowCLimit / UCLimit until 2026-10-30 —
     which are DIFFERENT columns legacy shows on the same grid, so two fields
     wore one name and a lab reading "LowCLimit" was not seeing LowCLimit. */
  surrogateAmount: "SurrogateAmount", surrogateUnits: "SurrogateUnits",
  surrogateLowLimit: "SurrogateLLimit", surrogateHighLimit: "SurrogateULimit",

  isResultDefaultValue: "IsResultDefaultValue",
  qcType: "QCType", qcRole: "QcRole", qcRootRole: "QCRootRole",
  rpdCtrlLimit: "RPDCtrlLimit", spikeAmount: "SpikeAmount", spikeUnits: "SpikeAmountUnits",
  recLcLimit: "RecLCLimit", recUcLimit: "RecUCLimit",
  rpdSigFig: "RPDSigFig", rpdCutOff: "RPDCutOff", rpdDecimal: "RPDDecimal",
  recSigFig: "RecSigFig", recCutOff: "RecCutOff", recDecimal: "RecDecimal",
  method: "Method", tier: "Tier", prepType: "PrepType", department: "Department",
  instrument: "Instrument", description: "Description", activeDate: "ActiveDate",
  excludeReport: "ExcludeReport", qcSource: "QC_Source",
  container: "Container", preservative: "Preservative", temperature: "Temperature",
  holdingTimeBeforePrep: "HoldingTimeBeforePrep",
  holdingTimeBeforeAnalysis: "HoldingTimeBeforeAnalysis",
  setPrepTimeAsAnalysisTime: "SetPrepTimeAsAnalysis",
  monitorHoldTime: "MonitorHoldTime", warnBeforeHours: "WarnBefore (h)",
};

/** Testparameter_ListView_Test_Surrogates — GoLive's 23, in its order, plus
 *  RetireDate.
 *
 *  ⚠ RetireDate is a DELIBERATE DEVIATION: GoLive hides it here (Index="-1").
 *  It was left off for that reason until 2026-10-30, when retiring the last
 *  surrogate made the row vanish with nothing on screen to say why — GoLive can
 *  afford to hide the date because it does not hide the ROW, while this grid
 *  shows active rows only. Without the column the retire is invisible, and it
 *  reads as a delete (owner report: "I retire the surrogate data but it was
 *  removed"). */
export const SURROGATE_KEYS = [
  "parameter", "sort", "internalStandard", "spikeAmount", "spikeUnits",
  "surrogateAmount", "surrogateLowLimit", "surrogateHighLimit", "recLcLimit", "recUcLimit",
  "mdl", "mcl", "lcLimit", "ucLimit", "reLcLimit", "reUcLimit",
  "sigFig", "cutOff", "decimal", "recSigFig", "recCutOff", "recDecimal", "comment",
  "retireDate",
] as const;

/** Testparameter_ListView_Test_InternalStandards — GoLive's 15, in its order.
 *  ⚠ LowCLimit here is the ORDINARY LowCLimit column (legacy captions it
 *  "LowCLimit" on this view and "LCLimit" on the others — same field). */
export const INTERNAL_STANDARD_KEYS = [
  "parameter", "sort", "spikeAmount", "spikeUnits", "rpdCtrlLimit",
  "mdl", "mcl", "lcLimit", "ucLimit", "reLcLimit", "reUcLimit",
  "sigFig", "cutOff", "decimal", "comment",
  // Same deviation, same reason as the Surrogates list above.
  "retireDate",
] as const;

/** Testparameter_ListView_Test_QCSampleParameter_QCParameterDefault — GoLive's
 *  27, plus QcRole, which AI shows and legacy does not: it is derived from the
 *  QC type and reads usefully beside it. */
export const QC_KEYS = [
  "qcType", "qcRole", "parameter", "synonym", "sort",
  "defaultResult", "defaultUnits", "spikeAmount", "spikeUnits",
  "rl", "mdl", "rpdCtrlLimit", "recLcLimit", "recUcLimit",
  "lcLimit", "ucLimit", "reLcLimit", "reUcLimit",
  "rpdSigFig", "rpdCutOff", "rpdDecimal",
  "recSigFig", "recCutOff", "recDecimal",
  "sigFig", "cutOff", "decimal", "comment",
] as const;

/** Testparameter_ListView_Test_SampleParameter — GoLive's 29. On screen this
 *  tab is four always-on columns plus four toggleable bands; the flat list here
 *  is what those add up to. */
export const SAMPLE_PARAMETER_KEYS = [
  "parameter", "surrogate", "internalStandard", "sort",
  "defaultResult", "defaultUnits", "finalDefaultResult", "finalDefaultUnits",
  "rl", "mdl", "mdlSpikeAmount", "mdlSpikeUnits",
  "rsd", "regulatoryLimit", "mcl", "loq", "uql", "lcLimit", "ucLimit", "reLcLimit", "reUcLimit",
  "sigFig", "cutOff", "decimal",
  "synonym", "accreditation", "comment", "isResultDefaultValue", "retireDate",
] as const;

/** GroupTestMethod_ListView_IsGroup — GoLive's 4. These are fixed headers, not
 *  LABEL keys, because the grid builds them inline. */
export const GROUP_TEST_HEADERS = ["Test", "Method", "Component", "Parameter"] as const;

const headers = (keys: readonly string[]) => keys.map((k) => LABEL[k] ?? k);

/** The header text each tab renders — what the parity test compares against
 *  GoLive's own column lists. */
export const TEST_CODE_TAB_COLUMNS = {
  sampleParameters: headers(SAMPLE_PARAMETER_KEYS),
  surrogates: headers(SURROGATE_KEYS),
  internalStandards: headers(INTERNAL_STANDARD_KEYS),
  qc: headers(QC_KEYS),
  groupTest: [...GROUP_TEST_HEADERS],
};
